/**
 * Selectors that join favorites state with other feature slices.
 * Import concrete reducer modules (not barrels) to avoid circular dependencies.
 */
import { createSelector } from '@ngrx/store';
import { assistantsFeature } from '../chats/assistants/store/assistants.reducer';
import { assistantFavoritesFeature } from './favorites.reducer';

/**
 * Favorite assistants joined with the assistants entity map, in persisted
 * fractional-indexing order. Rows with no loaded assistant entity are omitted.
 *
 * The join reads the raw map: a role-read skill stays as the user set it. The
 * default chat skill is hidden from members (`hiddenFromLists`, KD-12), but its
 * favorite row is kept, so the favorite returns when the skill stops being the
 * default.
 */
export const selectFavoriteAssistants = createSelector(
  assistantFavoritesFeature.selectFavoritesSorted,
  assistantsFeature.selectEntities,
  (favorites, assistantEntities) =>
    favorites
      .map((fav) => {
        const assistant = assistantEntities[fav.assistantId];
        if (!assistant || assistant.hiddenFromLists) return null;
        return {
          uuid: assistant.uuid,
          name: assistant.name,
          logo: assistant.logo,
          sortOrder: fav.sortOrder,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null),
);
